import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@kidshome/ui';
import { ErrorState } from '@/shared/ui';
import { reportRouteError } from '@/shared/lib/sentry';

/**
 * Root-level RR7 `errorElement` (FSD `app`). A render/loader throw used to white-screen the whole app;
 * this catches it and renders the shared, kind `ErrorState` with a reload + a "go home" link, instead
 * of a blank page. Applied to the `/` and `/admin` route trees.
 *
 * It deliberately does NOT depend on the app providers being mounted above it (a loader can throw
 * before they render in some flows), so it pulls `Button`/`ErrorState`/i18n directly — these resolve
 * against the i18n + theme already initialised at module load.
 */
export function RouteError(): React.ReactElement {
  const error = useRouteError();
  const { t } = useTranslation('common');

  // Report a render/loader throw to Sentry (M7 36a). RR7's error boundary swallows these, so the
  // SDK's global handlers never see them — we forward explicitly. A no-op when Sentry is disabled
  // (no VITE_SENTRY_DSN); PII is scrubbed in beforeSend. Skip a `redirect()` Response (control flow,
  // not an error). Fire-and-forget — reporting never blocks the recovery UI.
  useEffect(() => {
    if (error instanceof Response) {
      return;
    }
    void reportRouteError(error);
  }, [error]);

  // Reload re-runs the loaders from scratch (the most reliable recovery for a transient failure); the
  // home link gives a way out when a specific route is the problem.
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-bg p-6 text-text">
      <ErrorState />
      <div className="flex gap-2">
        <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
          {t('states.retry')}
        </Button>
        <Button variant="outline" size="sm" onClick={() => (window.location.href = '/')}>
          {t('nav.home')}
        </Button>
      </div>
      {/* Surface a short diagnostic in dev only; never leak internals to end users in production. */}
      {import.meta.env.DEV && error instanceof Error && (
        <pre className="max-w-md overflow-auto rounded-md bg-surface-muted p-3 text-xs text-text-muted">
          {error.message}
        </pre>
      )}
    </div>
  );
}
